import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import api from "../lib/api";

const DIM_DESC = {
  physical: "Energy, sleep, movement, recovery — the body's baseline capacity.",
  mental: "Focus, mood, resilience — clarity over low-grade overwhelm.",
  spiritual: "Meaning, stillness, connection to something larger.",
  relational: "The bonds that nourish rather than deplete you.",
  vocational: "Work and money returning value worthy of the energy spent.",
  aspirational: "A clear, felt sense of the next chapter you want to build.",
  legacy: "What you are shaping that will outlast and matter beyond you.",
};

const OUTPUTS = [
  { n: "01", t: "Imbalance Reading", s: "Where your capacity is quietly leaking most." },
  { n: "02", t: "Pathway", s: "Which of the Seven Pathways your life is asking for." },
  { n: "03", t: "Lifestay Format", s: "The container that fits — solo, intimate, or group." },
  { n: "04", t: "Matched Experience", s: "A signature or curated experience, chosen for you." },
];

export default function WellthspanIndex() {
  const [dims, setDims] = useState([]);
  useEffect(() => { api.get("/diagnostic/questions").then((r) => setDims(r.data.dimensions)); }, []);

  return (
    <div className="min-h-screen bg-ivory">
      <Header variant="light" active="experiences" />

      <section className="pt-32 pb-14 px-6 lg:px-10 max-w-7xl mx-auto">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="eyebrow mb-6">The EQ4 Process · A Diagnostic from LUXEWELL</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.03] tracking-tight max-w-4xl">
          THE WELLTHSPAN™<br />INDEX.
        </motion.h1>
        <p className="mt-8 font-headline italic text-xl lg:text-2xl text-ink/70 max-w-3xl leading-snug">
          Three spans, one life. A strategic measure of longevity, vitality and legacy — because how well a human flourishes is how well humanity flourishes.
        </p>
        <p className="font-headline italic text-3xl text-gold mt-4">Live long, live well, flourish.</p>
        <div className="mt-10 grid sm:grid-cols-3 gap-px bg-gold/25 border border-gold/25 max-w-3xl">
          {[
            { t: "Lifespan", s: "Live long" },
            { t: "Healthspan", s: "Live well" },
            { t: "WELLTHSPAN™", s: "Live long, live well, flourish" },
          ].map((x) => (
            <div key={x.t} className="bg-ivory p-6">
              <p className="eyebrow !text-ink/80 mb-2">{x.t}</p>
              <p className="font-headline italic text-lg text-gold">{x.s}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <Link to="/diagnostic" data-testid="wellthspan-begin" className="btn-lux bg-ink text-ivory hover:bg-gold hover:text-ink">Begin the Index</Link>
          <div className="flex items-center gap-6 text-xs text-ink/50">
            <span><span className="font-display text-gold text-lg">14</span> questions</span>
            <span className="hairline w-8" />
            <span><span className="font-display text-gold text-lg">~3</span> minutes</span>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-12 border-t border-gold/25">
        <p className="eyebrow mb-2">Seven Dimensions of a Life</p>
        <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-10">WHAT THE INDEX READS</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gold/25 border border-gold/25">
          {dims.map((d, i) => (
            <motion.div key={d.key} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.05 }} data-testid={`dim-${d.key}`} className="bg-ivory p-7">
              <p className="font-display text-4xl text-gold/30">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="font-headline text-xl mt-3 leading-tight">{d.label}</h3>
              <p className="text-sm text-ink/60 mt-2 leading-relaxed">{DIM_DESC[d.key]}</p>
            </motion.div>
          ))}
          <div className="bg-sage text-ivory p-7 flex flex-col justify-center">
            <p className="font-headline italic text-2xl leading-snug">One reading.<br />Seven dimensions.<br />One clear next move.</p>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-12 border-t border-gold/25">
        <p className="eyebrow mb-2">Your Reading Returns Four Things</p>
        <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-10">CLARITY, NOT A SCORE</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-gold/25 border border-gold/25">
          {OUTPUTS.map((o) => (
            <div key={o.n} className="bg-ivory p-7">
              <p className="font-display text-5xl text-gold/30">{o.n}</p>
              <h3 className="font-headline text-xl mt-3">{o.t}</h3>
              <p className="text-sm text-ink/60 mt-2 leading-relaxed">{o.s}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-12 border-t border-gold/25">
        <p className="eyebrow mb-8 text-center">One Index · Two Editions</p>
        <div className="grid md:grid-cols-2 gap-px bg-gold/25 border border-gold/25">
          <div className="bg-ivory p-8 lg:p-12">
            <p className="eyebrow mb-4">For Travellers & Seekers · B2C</p>
            <h3 className="font-display text-2xl lg:text-3xl tracking-tight">The Balanced Life Index</h3>
            <p className="mt-4 text-sm text-ink/65 leading-relaxed">Uncovering the imbalance across mind, body, soul, love and legacy — and the experience prescribed for your equilibrium.</p>
            <p className="eyebrow !text-[0.55rem] !text-ink/40 mt-5">Seven Chapters · 14 Questions · ~3 Minutes</p>
            <Link to="/diagnostic" data-testid="wellthspan-life-cta" className="btn-lux mt-6 inline-block bg-ink text-ivory hover:bg-gold hover:text-ink">Take the Life Index</Link>
          </div>
          <div className="bg-bone p-8 lg:p-12">
            <p className="eyebrow mb-4">For Leaders & Founders · B2B <span className="text-gold">— proposed</span></p>
            <h3 className="font-display text-2xl lg:text-3xl tracking-tight">The Balanced Business Index</h3>
            <p className="mt-4 text-sm text-ink/65 leading-relaxed">Where your leverage sits inside the $6.7 trillion industry — your archetype, your role, your route onto The LIST by LUXEWELL.</p>
            <p className="eyebrow !text-[0.55rem] !text-ink/40 mt-5">Four Chapters · 28 Questions · ~7 Minutes</p>
            <Link to="/list" className="btn-lux mt-6 inline-block bg-transparent text-ink hover:bg-gold/10">Explore The LIST</Link>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-16 text-center">
        <p className="font-editorial italic text-2xl lg:text-3xl text-ink/80 max-w-3xl mx-auto leading-snug">
          "You do not need another holiday. You need the right experience for the life you are living now."
        </p>
        <Link to="/diagnostic" data-testid="wellthspan-footer-cta" className="btn-lux mt-10 inline-block bg-ink text-ivory hover:bg-gold hover:text-ink">Begin the Index</Link>
      </section>

      <footer className="px-6 lg:px-10 max-w-7xl mx-auto py-8 border-t border-gold/25 text-ink/40 text-xs">
        WELLTHSPAN™ is a proprietary strategic self-assessment and intelligence tool — not a clinical, medical or psychometric diagnosis.
      </footer>
    </div>
  );
}
